import { ValueFormatterFunc, ValueFormatterParams } from "ag-grid-community";
import _, { get, isDate, isNull, isNumber, sumBy } from "lodash";
import { DateTime, DateTimeFormatOptions } from "luxon";

import {
  ApprovalTreeWithProposers,
  ProposalColumnFnParams,
  ProposerBudgetWithApprovalsAndProposals,
  ProposerBudgetWithProposals,
} from "frontend/utils/types";
import { DateString } from "lib/date/types";
import { annualizeCashValue } from "lib/shared/compensation";
import { EmploymentSubType } from "lib/shared/compensation/employment/types";
import { getConvertedCurrency } from "lib/shared/currency/getConvertedCurrency";
import { getCurrencyOffInstance } from "lib/shared/currency/getCurrencyField";
import { PayPointSettings } from "lib/shared/pay-points/types";
import { BudgetMode } from "lib/shared/review/types";
import { getBonusTargetInCentsForUser } from "lib/shared/users";
import { CompensationApprovalLevel, ProposerBudget, SalaryIncomeFrequency } from "prisma/cm/client";
import store from "store";
import { userOrReviewContextCurrencySelector } from "store/core";
import { LOCAL_CURRENCY, US_DOLLAR } from "utils/constants";

import {
  addProposerBudgetsToApprovalTree,
  BudgetField,
  budgetModeForField,
  buildApprovalTree,
  cellFormatterCurrency,
  doAppropriateRounding,
  doesApprovalTreeContainNumberBudgetValues,
  getSuggestedBudget,
  nullableUserEquityValueFormatter,
  redactedSymbol,
  simpleFormatNumber,
  sumAllActiveEquity,
  sumBudgetFromApprovalTree,
  SumReviewBudgetBudgetParams,
} from "./helpers";
import { Proposal } from "../../../shared/proposals/types";

export const formatNumberWithStoreDep = (value: number, currency?: string, decimals = 0) => {
  const storeCore = store.getState().core;
  const company = storeCore.user?.company;
  const locale = company?.locale ?? "en-US";

  const cleanedCurrency =
    currency !== LOCAL_CURRENCY
      ? currency
      : storeCore.review?.defaultCurrency ?? storeCore.user?.salaryCurrencyCode;

  return simpleFormatNumber(value, locale, {
    style: "currency",
    currency: cleanedCurrency || US_DOLLAR,
    currencyDisplay: locale === "en-US" ? "symbol" : "narrowSymbol",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

export const formatDateWithStoreDep = (
  value?: Date | DateString | string | null,
  options?: {
    format?: DateTimeFormatOptions;
    locale?: string;
  },
): string => {
  const parsed = isDate(value) ? DateTime.fromJSDate(value) : DateTime.fromISO(value || "");
  const company = store.getState().core.user?.company;

  return parsed.isValid
    ? new Date(parsed.toJSDate()).toLocaleDateString(
        options?.locale ?? company?.locale ?? "en-US",
        {
          ...options?.format,
        },
      )
    : "";
};

export const formatDateTimeWithStoreDep = (
  value?: Date | DateString | string | null,
  options?: {
    format?: DateTimeFormatOptions;
    locale?: string;
  },
): string => {
  const parsed = isDate(value) ? DateTime.fromJSDate(value) : DateTime.fromISO(value || "");
  const company = store.getState().core.user?.company;

  return parsed.isValid
    ? new Date(parsed.toJSDate()).toLocaleString(options?.locale ?? company?.locale ?? "en-US", {
        ...options?.format,
      })
    : "";
};

export const annualizeInUsdWithStoreDep = (
  salary: number,
  currencyCode: string,
  incomeFrequency: SalaryIncomeFrequency | null,
  employmentSubType: EmploymentSubType | null,
): number => {
  const exchangeRates = store.getState().core.review?.exchangeRates || {};

  const salaryInUsd = getConvertedCurrency({
    value: salary,
    options: {
      exchangeRates,
      sourceCurrency: currencyCode,
      targetCurrency: "USD",
    },
  }).raw;
  return annualizeCashValue({ value: salaryInUsd, incomeFrequency, employmentSubType });
};
export const sumFieldForProposalAnnualizedWithStoreDep = (
  proposals: Proposal[],
  field: string,
  toCurrency: string,
): number => {
  const exchangeRates = store.getState().core.review?.exchangeRates || {};

  const convertedRoundedNumbers = proposals.map((item) => {
    const unconvertedValue = get(item, field, 0) as number;

    if (!unconvertedValue || !item.owner || !item.owner.employmentSubType) {
      return {
        name: item.owner?.name ?? "",
        id: item.owner?.id ?? "",
        unconvertedValue,
        convertedValue: 0,
        convertedRoundedValue: 0,
        annualized: 0,
      };
    }

    const salaryIncomeFrequency = item.owner.salaryIncomeFrequency ?? SalaryIncomeFrequency.YEARLY;

    const employmentSubType = item.owner.employmentSubType as EmploymentSubType;

    const sourceCurrency = getCurrencyOffInstance(item.owner) ?? US_DOLLAR;
    const targetCurrency = toCurrency;

    const convertedValue = getConvertedCurrency({
      value: unconvertedValue,
      options: {
        exchangeRates,
        sourceCurrency,
        targetCurrency,
      },
    }).raw;

    const convertedRoundedValue = doAppropriateRounding(
      convertedValue,
      field,
      salaryIncomeFrequency,
    );

    const annualized = annualizeCashValue({
      value: convertedRoundedValue,
      incomeFrequency: salaryIncomeFrequency,
      employmentSubType,
    });

    return {
      name: item.owner.name,
      id: item.owner.id,
      sourceCurrency,
      targetCurrency,
      unconvertedValue,
      convertedValue,
      convertedRoundedValue,
      annualized,
    };
  });

  const sum = sumBy(convertedRoundedNumbers, (item) => {
    // console.log(item);
    return item.annualized;
  });

  // console.log({ sum });
  return sum;
};
export const sumFieldForProposalAnnualizedInUsdWithStoreDep = (
  proposals: Proposal[],
  field: string,
): number => {
  return sumBy(proposals, (item) => {
    const data = get(item, field, 0) as number;
    if (
      !data ||
      !item.owner ||
      (item.owner.salaryIncomeFrequency === SalaryIncomeFrequency.HOURLY &&
        !item.owner.employmentSubType)
    ) {
      return 0;
    }

    const sourceCurency = item.owner?.salaryCurrencyCode ?? US_DOLLAR;
    const salaryIncomeFrequency = item.owner.salaryIncomeFrequency ?? SalaryIncomeFrequency.YEARLY;
    const employmentSubType = item.owner.employmentSubType as EmploymentSubType;

    const annualizedUsdValue = annualizeInUsdWithStoreDep(
      data,
      sourceCurency,
      salaryIncomeFrequency,
      employmentSubType,
    );

    // console.log("annualizedUsdValue", {
    //   data,
    //   sourceCurency,
    //   salaryIncomeFrequency,
    //   employmentSubType,
    //   annualizedUsdValue
    // });
    return annualizedUsdValue;
  });
};
export const sumBudgetForApproversAndProposersWithStoreDep = (
  approvalLevels: CompensationApprovalLevel[],
  proposerBudgets: ProposerBudgetWithApprovalsAndProposals[],
  field: "raiseBudget" | "bonusBudget" | "equityBudget",
  rootLevelOrProposerBudget: CompensationApprovalLevel | ProposerBudget,
  baseCurrencyCode?: string,
) => {
  const review = store.getState().core.review;
  const exchangeRates = review?.exchangeRates || {};

  if (rootLevelOrProposerBudget && _.isNumber(rootLevelOrProposerBudget[field])) {
    const rootLevelFieldValue = rootLevelOrProposerBudget[field] ?? 0;

    if (field === "equityBudget") return rootLevelFieldValue;

    return getConvertedCurrency({
      value: rootLevelFieldValue,
      options: {
        exchangeRates,
        sourceCurrency: baseCurrencyCode ?? review?.defaultCurrency ?? US_DOLLAR,
        targetCurrency: US_DOLLAR,
      },
    }).raw;
  }

  // Hacky.  Checks to see if this is a proposer budget, and if it is, and
  // we've reached this point, we can return 0;
  // @ts-expect-error TS2339
  if (!rootLevelOrProposerBudget.approverId) return 0;

  // We need to build a tree to see if any approvers or proposers have budgets set.
  const approvalLevelTree: ApprovalTreeWithProposers = buildApprovalTree(
    approvalLevels,
    rootLevelOrProposerBudget as CompensationApprovalLevel,
  ) as ApprovalTreeWithProposers;

  addProposerBudgetsToApprovalTree(approvalLevelTree, proposerBudgets);

  // More hacky.  If budgetValue is 0, we need to iterate through the tree
  // and verify that 0 is the actual value set in the budget input.  If
  // the input was left empty, the sum method above will still return 0, but what
  // we actually want is null.
  if (!doesApprovalTreeContainNumberBudgetValues(approvalLevelTree, field)) {
    return null;
  }

  const budgetSum = sumBudgetFromApprovalTree(approvalLevelTree, field) ?? 0;

  if (field === "equityBudget") {
    return budgetSum;
  }

  // Returning value in USD because convert function used later on
  // uses US_DOLLAR as the default base.
  return getConvertedCurrency({
    value: budgetSum,
    options: {
      exchangeRates,
      sourceCurrency: baseCurrencyCode ?? review?.defaultCurrency ?? US_DOLLAR,
      targetCurrency: US_DOLLAR,
    },
  }).raw;
};
export const sumProposedAmountsFromApprovalTreeWithStoreDep = (
  approvalTree: ApprovalTreeWithProposers,
  field: "raise" | "bonus" | "equity" | "oteIncrease",
) => {
  let value = 0;

  approvalTree.proposers.forEach((proposer) => {
    // @ts-expect-error TS2339
    if (proposer.proposals.length) {
      // @ts-expect-error TS2339
      value += sumProposalValuesByFieldWithStoreDep(proposer.proposals, field);
    }
  });

  approvalTree.children.forEach((item) => {
    value += sumProposedAmountsFromApprovalTreeWithStoreDep(
      item as ApprovalTreeWithProposers,
      field,
    );
  });

  return value;
};
export const sumProposalValuesByFieldWithStoreDep = (
  proposals: Proposal[],
  field: "raise" | "bonus" | "equity" | "oteIncrease",
) => {
  if (field === "equity") {
    return sumAllActiveEquity(proposals);
  } else if (field === "raise" || field === "oteIncrease") {
    return sumFieldForProposalAnnualizedInUsdWithStoreDep(proposals, field);
  } else {
    return sumProposalDataWithStoreDep(proposals, field);
  }
};

export function calculateBonusTargetInCentsWithStoreDep(
  proposerBudget: ProposerBudgetWithProposals,
) {
  // @ts-expect-error TS2345
  return sumBy(proposerBudget.proposals as Proposal[], (item: Proposal) => {
    return getBonusTargetInCentsForUser(item.owner);
  });
}
export const dateValueFormatterWithStoreDep = (params: ValueFormatterParams) =>
  formatDateWithStoreDep(params.value);
export const decimalCurrencyValueFormatterWithStoreDep: ValueFormatterFunc = (
  params: ValueFormatterParams,
) => formatNumberWithStoreDep(params.value, US_DOLLAR, 2);
export const sumTotalBudgetsForRootApprovalLevelsAndProposersWithStoreDep = (
  levels: CompensationApprovalLevel[],
  proposerBudgets: ProposerBudget[],
  field: "raiseBudget" | "bonusBudget" | "equityBudget",
) => {
  const startingDepth = levels.map((level) => level.depth).sort()[0];
  const rootLevels = levels.filter((level) => level.depth === startingDepth);

  let budgetValue: number | null = null;
  rootLevels.forEach((level) => {
    const budgetSumForLevel = sumBudgetForApproversAndProposersWithStoreDep(
      levels,
      // @ts-expect-error TS2345
      proposerBudgets,
      field,
      level,
    );
    if (isNumber(budgetSumForLevel)) {
      if (isNull(budgetValue)) {
        budgetValue = 0;
      }

      budgetValue += budgetSumForLevel;
    }
  });

  return budgetValue;
};
export const sumProposalDataForApproversWithStoreDep = (
  approvalLevels: CompensationApprovalLevel[],
  proposerBudgets: ProposerBudgetWithApprovalsAndProposals[],
  rootApprovalLevel: CompensationApprovalLevel,
  field: "raise" | "bonus" | "equity" | "oteIncrease",
) => {
  // We need to build a tree to see if any approvers or proposers have budgets set.
  const approvalLevelTree: ApprovalTreeWithProposers = buildApprovalTree(
    approvalLevels,
    rootApprovalLevel,
  ) as ApprovalTreeWithProposers;

  addProposerBudgetsToApprovalTree(approvalLevelTree, proposerBudgets);

  // returns values converted to USD (if not equity)
  return sumProposedAmountsFromApprovalTreeWithStoreDep(approvalLevelTree, field);
};

export const sumReviewBudgetWithStoreDep = (
  budgets: SumReviewBudgetBudgetParams,
  field: BudgetField,
  currentlySelectedCurrency: string = US_DOLLAR,
) => {
  const review = store.getState().core.review;

  if (!review) return 0;

  const exchangeRates = review?.exchangeRates || {};

  const result = sumBy(budgets, (item) => {
    const sourceCurrency = review?.defaultCurrency ?? US_DOLLAR;
    const targetCurrency = currentlySelectedCurrency;

    let unconvertedValue;
    const budgetMode = budgetModeForField(review, field);

    if (budgetMode === BudgetMode.SUM_OF_SUGGESTED) {
      const suggestedBudget = getSuggestedBudget({
        // @ts-expect-error TS2322
        proposerBudget: item,
        field,
        review,
        targetCurrency,
      });
      return suggestedBudget;
    } else {
      unconvertedValue = get(item, field, 0);

      const convertedValue = getConvertedCurrency({
        value: unconvertedValue,
        options: {
          exchangeRates,
          sourceCurrency,
          targetCurrency,
        },
      }).raw;

      const roundedConvertedValue = doAppropriateRounding(
        convertedValue,
        field,
        // @ts-expect-error TS2339
        item?.owner?.salaryIncomeFrequency,
      );

      return roundedConvertedValue;
    }
  });

  return result;
};
export const sumProposalDataWithStoreDep = (
  proposals: Proposal[],
  field: string,
  currencyCode: string = US_DOLLAR,
) => {
  const exchangeRates = store.getState().core.review?.exchangeRates || {};

  return sumBy(proposals, (item) => {
    const unconvertedValue = get(item, field, 0);

    const sourceCurrency = item.owner?.salaryCurrencyCode ?? US_DOLLAR;
    const targetCurrency = currencyCode;

    const convertedValue = getConvertedCurrency({
      value: unconvertedValue,
      options: {
        exchangeRates,
        sourceCurrency,
        targetCurrency,
      },
    }).raw;

    return convertedValue;
  });
};

export const getExportFileNameWithStoreDep = ({
  cycleName,
  exportName,
  fileExtension,
}: {
  cycleName?: string;
  exportName: string;
  fileExtension: string;
}) => {
  const uiCurrency = userOrReviewContextCurrencySelector(store.getState());
  return `${exportName}${cycleName ? " - " + cycleName + " - " : " - Comprehensive Export - "}${
    store.getState().core.user?.company.name
  }(${uiCurrency === LOCAL_CURRENCY ? "Local Currency" : uiCurrency}).${fileExtension}`;
};

export function nullablePayPointValueFormatterWithStoreDep(params: ValueFormatterParams) {
  if (params.value > 0) {
    /*
     * NB: We supply some defense here as this helper is used on both Ranges and on Proposals and Users,
     * so the sourceCurrency is located in different places. Probably best to break this up
     * but for now we leave it.
     */
    let sourceCurrency: string | undefined;
    switch (params.data.explicitKind) {
      case "user":
        sourceCurrency = params.data.salaryCurrencyCode;
        break;
      case "proposal":
        // isaac: we probably shouldnt cascade this way - i moved to a switch on kind when i added support for employees and didnt want to risk breaking something
        sourceCurrency =
          params.data.owner?.salaryCurrencyCode || params.data.salaryCurrencyCode || US_DOLLAR;
        break;
      case "range":
        // isaac: we probably shouldnt cascade this way - i moved to a switch on kind when i added support for employees and didnt want to risk breaking something
        sourceCurrency =
          params.data.owner?.salaryCurrencyCode || params.data.salaryCurrencyCode || US_DOLLAR;
        break;
      default:
        console.warn("Unknown explicitKind, guessing at source", params.data.explicitKind);
        sourceCurrency =
          params.data.owner?.salaryCurrencyCode || params.data.salaryCurrencyCode || US_DOLLAR;
    }

    // isaac: this is either or hack or im clever but on the coldef you can optionally set path to source currency code.
    // use case: in the user mode list table the salary code of the user might not match the salary code of the range, so we cannot
    // just infer the path from the type of object we're using as the row data for all cases.
    // @ts-expect-error TS2339
    if (params?.colDef?.pathToCurrencyCode) {
      // @ts-expect-error TS2339
      let path = params.colDef.pathToCurrencyCode;

      // if params.colDef.pathToCurrencyCode is a function we call it with the params to get the value
      if (typeof path === "function") {
        path = path(params);
      }

      sourceCurrency = get(params.data, path, sourceCurrency);
    }

    const selectedCurrency = userOrReviewContextCurrencySelector(store.getState());
    const targetCurrency =
      selectedCurrency === "LOCAL_CURRENCY" ? sourceCurrency : selectedCurrency;
    return cellFormatterCurrency(params.context.convert, params.data, params.value, {
      decimalField: "salaryIncomeFrequency",
      sourceCurrency,
      targetCurrency,
    }).formatted;
  }
  if (params.value === redactedSymbol) return redactedSymbol;
  return "-";
}
export function getValueFormatterForPayPointGroupWithStoreDep(
  payPointGroupName: keyof PayPointSettings,
): ((params: ProposalColumnFnParams) => any) | undefined {
  const isEquityColumn = _.includes(
    ["newHireGrant", "promoGrant", "refreshGrant"],
    payPointGroupName,
  );

  if (isEquityColumn) {
    return nullableUserEquityValueFormatter;
  }

  if (_.includes(["salary", "totalPay", "variable"], payPointGroupName)) {
    return nullablePayPointValueFormatterWithStoreDep;
  }

  if (payPointGroupName === "bonus") {
    // todo: how do we format bonus?
    // temp solution for displaying bonus values that are between 0 and 1, and values that greater than 1.
    return (params: ValueFormatterParams) => {
      const value = params.value <= 1 ? params.value * 100 : params.value;
      const percentString = `${value?.toFixed(2)}%`;
      return percentString;
    };
  }

  return undefined;
}
